// src/components/OnboardingTooltip.tsx
import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Info, X } from 'lucide-react';
import { useOnboarding } from '../constants/onboarding';

interface Props {
  itemName: string;
  targetElement: HTMLElement | null;
  targetRect?: { top: number; left: number; bottom: number; right: number; width: number; height: number } | null;
  onSkip: () => void;
  onViewDetails: () => void;
}

type Placement = 'right' | 'left' | 'below' | 'above';

const OnboardingTooltip: React.FC<Props> = ({
  itemName,
  targetElement,
  targetRect,
  onSkip,
  onViewDetails,
}) => {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [placement, setPlacement] = useState<Placement>('right');
  const { onboardingData } = useOnboarding();

  const content = onboardingData[itemName];
  if (!content || !targetElement) return null;

  useEffect(() => {
    const positionTooltip = () => {
      const tooltip = tooltipRef.current;
      if (!tooltip) return;

      const rect = targetRect && targetRect.width > 0
        ? targetRect
        : targetElement.getBoundingClientRect();

      if (rect.width === 0 && rect.height === 0) return;

      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const gap = 14;
      const margin = 16;

      tooltip.style.position = 'fixed';
      tooltip.style.zIndex = '9999';
      tooltip.style.maxWidth = '340px';
      tooltip.style.width = 'auto';

      const tooltipWidth = tooltip.offsetWidth;
      const tooltipHeight = tooltip.offsetHeight;
      let nextPlacement: Placement = 'right';

      if (viewportWidth >= 1024) {
        // Sidebar: bubble em frente (à direita) do item de menu
        let left = rect.right + gap;
        let top = rect.top + rect.height / 2;

        if (left + tooltipWidth > viewportWidth - margin) {
          left = rect.left - tooltipWidth - gap;
          nextPlacement = 'left';
        } else {
          nextPlacement = 'right';
        }

        top = Math.max(margin, Math.min(top, viewportHeight - tooltipHeight - margin));

        tooltip.style.left = `${left}px`;
        tooltip.style.top = `${top}px`;
        tooltip.style.transform = 'translateY(-50%)';
      } else {
        // Drawer: bubble em frente (por baixo) do item de menu
        let top = rect.bottom + gap;
        let left = rect.left;

        if (top + tooltipHeight > viewportHeight - margin) {
          top = rect.top - tooltipHeight - gap;
          nextPlacement = 'above';
        } else {
          nextPlacement = 'below';
        }

        left = Math.max(margin, Math.min(left, viewportWidth - tooltipWidth - margin));

        tooltip.style.left = `${left}px`;
        tooltip.style.top = `${top}px`;
        tooltip.style.transform = 'none';
        tooltip.style.maxWidth = `${Math.min(340, viewportWidth - margin * 2)}px`;
      }

      setPlacement(nextPlacement);
    };

    positionTooltip();
    window.addEventListener('resize', positionTooltip);
    return () => window.removeEventListener('resize', positionTooltip);
  }, [targetElement, targetRect]);

  const arrowStyle = (() => {
    const base: React.CSSProperties = { position: 'absolute', width: 0, height: 0 };
    switch (placement) {
      case 'right':
        return { ...base, left: '-8px', top: '50%', transform: 'translateY(-50%)',
          borderTop: '6px solid transparent', borderBottom: '6px solid transparent', borderRight: '8px solid #ffffff' };
      case 'left':
        return { ...base, right: '-8px', top: '50%', transform: 'translateY(-50%)',
          borderTop: '6px solid transparent', borderBottom: '6px solid transparent', borderLeft: '8px solid #ffffff' };
      case 'below':
        return { ...base, top: '-8px', left: '24px',
          borderLeft: '6px solid transparent', borderRight: '6px solid transparent', borderBottom: '8px solid #ffffff' };
      case 'above':
        return { ...base, bottom: '-8px', left: '24px',
          borderLeft: '6px solid transparent', borderRight: '6px solid transparent', borderTop: '8px solid #ffffff' };
    }
  })();

  return (
    <AnimatePresence>
      <motion.div
        ref={tooltipRef}
        initial={{ opacity: 0, y: 8, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 8, scale: 0.95 }}
        transition={{ duration: 0.2 }}
        className="relative bg-white shadow-xl rounded-2xl p-5 border border-slate-100 max-w-[300px]"
      >
        <div style={arrowStyle} />
        <div className="flex items-start gap-3">
          <div className="mt-0.5 bg-indigo-100 text-indigo-600 p-1.5 rounded-xl">
            <Info size={18} />
          </div>
          <div className="flex-1">
            <p className="text-slate-700 text-[15px] leading-snug">
              {content.shortDescription}
            </p>
          </div>
          <button
            onClick={onSkip}
            className="text-slate-400 hover:text-slate-500 -mt-1 -mr-1 p-1"
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex gap-3 mt-5">
          <button
            onClick={onSkip}
            className="flex-1 py-2.5 text-sm font-medium text-slate-500 hover:bg-slate-100 rounded-xl transition-colors"
          >
            Ignorar
          </button>
          <button
            onClick={onViewDetails}
            className="flex-1 py-2.5 text-sm font-medium bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-colors"
          >
            Ver tutorial
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default OnboardingTooltip;
